import React from 'react';
import { Box, Button, CircularProgress, useMediaQuery } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SkeletonTable from './SkeletonTable';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import PermissionState from './PermissionState';
import BulkBar from './BulkBar';
import DataTableCardMobile from './DataTableCardMobile';
import { radius } from '../../themes/smartinventoryTheme';

// Wraps MUI DataGrid in the rounded surface and OWNS the loading / empty /
// error / permission states so no page re-implements them. Slots:
//   toolbar   search · FilterChips · density · export — sits above the card,
//             rendered whenever the grid itself is showing (not during a
//             page-level error/permission takeover).
//   bulkBar   pass `bulkBar={{ count, countLabel, actions, onClear }}`;
//             renders nothing when count is 0. Docks above the card too, so
//             the card's own height never shifts.
//   loadMore  pass `loadMore={{ label, onClick, loading }}` for the "Load N
//             more · M remaining" control below the grid/cards — this is
//             the one pagination affordance the design uses; never add a
//             second pagination control alongside it.
//
// `status` drives which of the five states renders: 'loading' | 'error' |
// 'permission' | 'ready'. When status="ready" and `rows` is empty, `empty`
// takes over (kind="none" vs "no-match" is the caller's call — only the page
// knows whether a filter/search is active). DataGrid already carries its own
// border/radius from the theme, so the extra card surface below is only for
// the non-grid placeholder states.
const DataTableCard = ({
  status = 'ready',
  rows,
  columns,
  toolbar,
  bulkBar,
  empty,
  error,
  permission,
  skeletonColumns,
  loadMore,
  ...dataGridProps
}) => {
  const showBulkBar = bulkBar && bulkBar.count > 0;
  const showGrid = status === 'ready' && rows.length > 0;
  // Below `sm` (900px, matching the theme's custom breakpoints) the grid's
  // columns can't fit without horizontal scroll, so rows render as stacked
  // cards instead (see DataTableCardMobile). A literal query string is used
  // instead of the theme-callback form so this still works in tests that
  // render pages without a ThemeProvider in the tree.
  const isMobile = useMediaQuery('(max-width:899.95px)');
  // Tablet (900–1199px, `md`) keeps the grid but not every column fits at
  // that width — a column marked `hideOnTablet` drops out here so the grid
  // never scrolls sideways; it's still shown on mobile's card view.
  const isTablet = useMediaQuery('(max-width:1199.95px)') && !isMobile;
  const gridColumns = isTablet ? columns.filter((c) => !c.hideOnTablet) : columns;

  return (
    <Box>
      {status !== 'error' && status !== 'permission' && toolbar && (
        <Box sx={{ mb: 1.5 }}>{toolbar}</Box>
      )}
      {showBulkBar && status === 'ready' && <BulkBar {...bulkBar} />}

      {showGrid ? (
        <>
          {isMobile ? (
            <DataTableCardMobile rows={rows} columns={columns} {...dataGridProps} />
          ) : (
            <DataGrid rows={rows} columns={gridColumns} autoHeight hideFooter {...dataGridProps} />
          )}
          {loadMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={loadMore.onClick}
                disabled={loadMore.loading}
                startIcon={loadMore.loading ? <CircularProgress size={16} /> : undefined}
                sx={{ borderRadius: `${radius.pill}px` }}
              >
                {loadMore.label}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: `${radius.xl}px`, overflow: 'hidden', bgcolor: 'background.paper' }}>
          {status === 'loading' && <SkeletonTable columns={skeletonColumns} />}
          {status === 'error' && (
            <ErrorState scope="page" title={error.title} description={error.description} request={error.request} onRetry={error.onRetry} retryLabel={error.retryLabel} />
          )}
          {status === 'permission' && (
            <PermissionState title={permission.title} description={permission.description} action={permission.action} />
          )}
          {status === 'ready' && rows.length === 0 && (
            <EmptyState kind={empty.kind} icon={empty.icon} title={empty.title} description={empty.description} actions={empty.actions} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default DataTableCard;
